import type { AccessTokenClaims } from './session.js';
import { parseAccessTokenClaims } from './session.js';

export const IMPERSONATION_HANDOFF_READY = 'beatq:impersonation-ready';
export const IMPERSONATION_HANDOFF_TOKEN = 'beatq:impersonation-token';

const ENDED_GRANT_KEY = 'beatq.impersonation.endedGrantId';

export type ImpersonationClaim = {
  grantId?: string;
  actorSub?: string;
  readOnly?: boolean;
  by?: string;
  ref?: string;
  ro?: boolean;
};

export type ImpersonationSessionView = {
  grantId: string;
  actorLabel: string;
  readOnly: boolean;
  expiresAt: string | null;
};

function asImpClaim(raw: unknown): ImpersonationClaim | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as ImpersonationClaim;
}

export function impersonationClaimFromToken(token: string | null): ImpersonationClaim | null {
  const claims = parseAccessTokenClaims(token) as AccessTokenClaims & { imp?: ImpersonationClaim };
  return asImpClaim(claims.imp) ?? null;
}

export function impersonationViewFromToken(
  token: string | null,
  expiresAtMs?: number | null,
): ImpersonationSessionView | null {
  const imp = impersonationClaimFromToken(token);
  if (!imp) return null;
  const grantId = (imp.grantId ?? imp.ref ?? '').trim();
  if (!grantId) return null;
  const actorLabel = (imp.actorSub ?? imp.by ?? 'Support').trim() || 'Support';
  const readOnly = imp.readOnly === true || imp.ro === true;
  return {
    grantId,
    actorLabel,
    readOnly,
    expiresAt: expiresAtMs ? new Date(expiresAtMs).toISOString() : null,
  };
}

export function readEndedImpersonationGrantId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(ENDED_GRANT_KEY);
}

export function markImpersonationGrantEnded(grantId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(ENDED_GRANT_KEY, grantId);
}

export function clearEndedImpersonationGrant(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(ENDED_GRANT_KEY);
}

export function shouldIgnoreImpersonationHandoff(grantId: string | undefined | null): boolean {
  const ended = readEndedImpersonationGrantId();
  return Boolean(ended && grantId && ended === grantId);
}

export function isImpersonationHandoffTokenMessage(
  data: unknown,
): data is { type: typeof IMPERSONATION_HANDOFF_TOKEN; accessToken: string } {
  if (data == null || typeof data !== 'object') return false;
  const row = data as Record<string, unknown>;
  return row.type === IMPERSONATION_HANDOFF_TOKEN && typeof row.accessToken === 'string';
}

export function isImpersonationHandoffReadyMessage(data: unknown): boolean {
  if (data == null || typeof data !== 'object') return false;
  return (data as Record<string, unknown>).type === IMPERSONATION_HANDOFF_READY;
}

/** Local DEV only — never inlined into production builds (`import.meta.env.DEV` is false there). */
const DEV_SUBJECT_APP_ORIGINS = {
  customer: 'http://localhost:5173',
  merchant: 'http://localhost:5174',
  business: 'http://localhost:5176',
} as const;

export function subjectAppOrigin(targetSurface: string | null | undefined): string | null {
  if (typeof import.meta === 'undefined') return null;
  const explicit =
    targetSurface === 'merchant'
      ? import.meta.env.VITE_MERCHANT_ORIGIN
      : targetSurface === 'business'
        ? import.meta.env.VITE_BUSINESS_ORIGIN
        : import.meta.env.VITE_CUSTOMER_ORIGIN;
  const fromKey = explicit?.replace(/\/$/, '');
  if (fromKey) return fromKey;
  if (targetSurface === 'customer' || !targetSurface) {
    const fromSite = import.meta.env.VITE_SITE_ORIGIN?.replace(/\/$/, '');
    if (fromSite) return fromSite;
  }
  if (import.meta.env.DEV) {
    if (targetSurface === 'merchant') return DEV_SUBJECT_APP_ORIGINS.merchant;
    if (targetSurface === 'business') return DEV_SUBJECT_APP_ORIGINS.business;
    return DEV_SUBJECT_APP_ORIGINS.customer;
  }
  return null;
}
